import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionOverview = ({ limitStatus, subscription, onUpgrade }) => {
  const activeAgents = limitStatus?.active_agents || 0;
  const maxAgents = limitStatus?.max_agents || 2;
  const isCompliant = limitStatus?.is_compliant;
  const gracePeriodEnd = limitStatus?.grace_period_end;
  const agentsToDeactivate = limitStatus?.agents_to_deactivate || 0;

  const usagePercentage = (activeAgents / maxAgents) * 100;
  const isNearLimit = usagePercentage >= 80;
  const isOverLimit = activeAgents > maxAgents;

  const getProgressColor = () => {
    if (isOverLimit) return 'bg-red-500';
    if (isNearLimit) return 'bg-amber-500';
    return 'bg-primary';
  };

  const formatGracePeriod = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const gracePeriodRemaining = formatGracePeriod(gracePeriodEnd);

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-1 font-heading">
            Subscription Overview
          </h2>
          <p className="text-sm text-muted-foreground">
            Current plan: <span className="font-semibold text-foreground">{subscription?.subscription_plans?.name || 'Free'}</span>
          </p>
        </div>
        {!isCompliant && (
          <Button
            variant="default"
            size="sm"
            onClick={onUpgrade}
            iconName="ArrowUp"
            iconPosition="right"
          >
            Upgrade Plan
          </Button>
        )}
      </div>

      {/* Agent Usage Progress */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="Users" size={20} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Active Agents</span>
          </div>
          <span className="text-lg font-bold text-foreground">
            {activeAgents} / {maxAgents}
          </span>
        </div>
        
        <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getProgressColor()}`}
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          />
        </div>

        {isOverLimit && (
          <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <Icon name="AlertTriangle" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-500 mb-1">
                Agent Limit Exceeded
              </p>
              <p className="text-xs text-red-500/80">
                You need to deactivate {agentsToDeactivate} agent{agentsToDeactivate > 1 ? 's' : ''} to comply with your plan.
              </p>
            </div>
          </div>
        )}

        {isNearLimit && !isOverLimit && (
          <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <Icon name="AlertCircle" size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-500 mb-1">
                Approaching Agent Limit
              </p>
              <p className="text-xs text-amber-500/80">
                You're using {activeAgents} of {maxAgents} available agents. Consider upgrading for more capacity.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Grace Period Warning */}
      {gracePeriodEnd && gracePeriodRemaining && (
        <div className="flex items-start gap-3 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
          <Icon name="Clock" size={20} className="text-orange-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-orange-500 mb-1">
              Grace Period Active
            </p>
            <p className="text-xs text-orange-500/80 mb-2">
              You have {gracePeriodRemaining} remaining to deactivate {agentsToDeactivate} agent{agentsToDeactivate > 1 ? 's' : ''} or upgrade your plan.
            </p>
            <p className="text-xs text-orange-500/70">
              After the grace period, you won't be able to use agents until you're compliant.
            </p>
          </div>
        </div>
      )}

      {/* Upgrade CTA */}
      {(isNearLimit || isOverLimit) && (
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg">
          <div>
            <p className="text-sm font-semibold text-foreground mb-1">
              Need more agents?
            </p>
            <p className="text-xs text-muted-foreground">
              Upgrade to unlock up to {maxAgents === 2 ? '4' : '25'} agents
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={onUpgrade}
            iconName="Zap"
            iconPosition="left"
          >
            View Plans
          </Button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionOverview;